import {
  useQuery,
  UseQueryOptions,
  QueryKey,
  useMutation,
  useQueryClient,
  UseMutationOptions,
} from "@tanstack/react-query";
import getBITSEndApiMap from "../apiMap";
import { api } from "../services/AxiosService";
// Types
export interface TimeExtensionParams {
  page?: number;
  limit?: number;
  status?: "pending" | "approved" | "rejected";
  token?: string;
}

export interface Student {
  id: string;
  name: string;
  email: string;
}

export interface Project {
  id: string;
  title: string;
  course_id: string;
}

export interface SandboxSession {
  id: string;
  user_id: string;
  project_id: string;
  startTime: string;
  endTime: string;
  duration: number;
  lastHeartbeat: string;
  status: "active" | "closed" | "timed_out";
  extension_requested: boolean;
  createdAt: string;
  updatedAt: string;
  project: Project;
}

export interface TimeExtension {
  id: string;
  sandbox_session_id: string;
  student_id: string;
  reason: string;
  duration_value: number;
  duration_unit: "hours" | "days" | "minutes";
  status: "pending" | "approved" | "rejected";
  reviewed_by: string | null;
  reviewed_at: string;
  review_comments: string | null;
  approved_due_date: string | null;
  created_at: string;
  updated_at: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  sandboxSession_id: string;
  student: Student;
  sandboxSession: SandboxSession;
  reviewer?: {
    name: string;
  } | null;
}
export interface ActiveSandboxSession {
  id: string;
  lastSubmitted: string;
  duration: number;
  status: string;
  totalTime: string;
  totalUsedTime: number;
  remainingTime: number;
  user: {
    name: string;
    email: string;
    id: string;
  };
  projectAndCourseDetail: {
    id: string;
    title: string;
    course_id: string;
    sandbox_time_duration: string;
    course: {
      code: string;
      name: string;
      id: string;
    };
  };

  // ... other fields
}
export interface ActiveSandboxDropdownFields {
  project: {
    id?: string | null;
    title?: string;
  }[];

  course: {
    id?: string | null;
    name?: string;
    code?: string;
  }[];

  student: {
    id?: string | null;
    name?: string;
    email?: string;
  }[];
  sandboxSessions: [];
}

export interface PaginatedSandboxResponse {
  activeList: ActiveSandboxSession[];
  pagination: {
    currentPage: number;
    itemsPerPage: number;
    totalItems: number;
    totalPages: number;
  };
}
export interface ActiveDropdownResponse {
  list: ActiveSandboxDropdownFields;
}
export interface ActiveSandboxResponse {
  isSuccess: string;
  data: PaginatedSandboxResponse;
  message: string;
}
export interface ActiveSandboxDropdown {
  isSuccess: string;
  data: ActiveDropdownResponse;
  message: string;
}
export interface TimeExtensionResponse {
  data: {
    rows: TimeExtension[];
    count: number;
    page: number;
    totalPages: number;
  };
}

export type TimeExtensionResponseData = TimeExtensionResponse;
// for getActive sandbox
interface GetActiveSandboxParams {
  selectedProject: string;
  selectedCourse: string;
  selectedStudent: string;
  currentPage: number;
  itemsPerPage: number;
}
// for filtered project
export interface FilteredEntities {
  projects: DropdownOption[];
  students: DropdownOption[];
}
// Fetch function
export async function getTimeExtensions(
  params: TimeExtensionParams = {},
): Promise<TimeExtensionResponseData> {
  try {
    const url = getBITSEndApiMap("getTimeExtensionRequests");
    const { token, ...apiParams } = params;
    const { data } = await api.get<TimeExtensionResponse>(url, {
      params: apiParams,
    });

    return data;
  } catch (error) {
    throw error;
  }
}

// Update time extension status types
export interface UpdateTimeExtensionStatusParams {
  status: "approved" | "rejected";
  review_comments: string;
  reviewed_by: string;
  duration_value?: string;
}

export interface UpdateTimeExtensionStatusResponse {
  success: boolean;
  message: string;
  data?: TimeExtension;
}

/**
 * Update the status of a time extension request
 * @param id - The time extension request ID
 * @param params - Status update parameters
 * @example
 * ```typescript
 * await updateTimeExtensionStatus("123", {
 *   status: "approved",
 *   review_comments: "Valid reason — approving 2 extra days."
 * });
 * ```
 */
export async function updateTimeExtensionStatus(
  id: string,
  params: UpdateTimeExtensionStatusParams,
): Promise<UpdateTimeExtensionStatusResponse> {
  try {
    const baseUrl = getBITSEndApiMap("getTimeExtensionRequests");
    const url = `${baseUrl}/${id}/status`;

    const { data } = await api.put<UpdateTimeExtensionStatusResponse>(
      url,
      params,
    );
    return data;
  } catch (error) {
    throw error;
  }
}

// React Query hook for fetching time extensions
export function useTimeExtensionsQuery(
  params: TimeExtensionParams,
  options?: Partial<
    UseQueryOptions<
      TimeExtensionResponseData,
      Error,
      TimeExtensionResponseData,
      QueryKey
    >
  >,
) {
  return useQuery<
    TimeExtensionResponseData,
    Error,
    TimeExtensionResponseData,
    QueryKey
  >({
    queryKey: ["timeExtensions", params] as QueryKey,
    queryFn: () => getTimeExtensions(params),
    ...(options as any),
  });
}

/**
 * React Query mutation hook for updating time extension status
 * Automatically invalidates and refetches time extension queries on success
 * @example
 * ```typescript
 * const updateMutation = useUpdateTimeExtensionStatus({
 *   onSuccess: () => toast.success("Status updated!"),
 *   onError: (error) => toast.error(error.message)
 * });
 *
 * updateMutation.mutate({
 *   id: "123",
 *   params: { status: "approved", review_comments: "Looks good!" }
 * });
 * ```
 */
export function useUpdateTimeExtensionStatus(
  options?: Partial<
    UseMutationOptions<
      UpdateTimeExtensionStatusResponse,
      Error,
      { id: string; params: UpdateTimeExtensionStatusParams }
    >
  >,
) {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateTimeExtensionStatusResponse,
    Error,
    { id: string; params: UpdateTimeExtensionStatusParams }
  >({
    mutationFn: ({ id, params }) => updateTimeExtensionStatus(id, params),
    onSuccess: () => {
      // Invalidate and refetch time extensions queries
      queryClient.invalidateQueries({ queryKey: ["timeExtensions"] });
    },
    ...options,
  });
}
export async function getActiveSandboxList(
  params: GetActiveSandboxParams,
): Promise<PaginatedSandboxResponse> {
  try {
    const baseUrl = getBITSEndApiMap("getActiveSandboxList");
    // const url = `${baseUrl}`;
    let user = localStorage.getItem("auth_user");
    if (user === null) throw new Error("User not found in localStorage");
    let userDetail = JSON.parse(user);

    if (userDetail?.role === "student") {
      throw new Error(
        "Access denied: The user cannot view the active sandbox list",
      );
    }

    const response = await api.get<ActiveSandboxResponse>(baseUrl, { params });

    return response.data.data;
  } catch (error) {
    throw error;
  }
}
export function getAllActiveSandbox(param: GetActiveSandboxParams) {
  return useQuery({
    queryKey: [
      "activeSandbox",
      param.currentPage,
      param.itemsPerPage,
      param.selectedCourse,
      param.selectedProject,
      param.selectedStudent,
    ],
    queryFn: () => {
      const params: any = {
        page: param.currentPage,
        limit: param.itemsPerPage,
      };
      if (param.selectedCourse && param.selectedCourse !== "all")
        params.courseId = param.selectedCourse;
      if (param.selectedProject && param.selectedProject !== "all")
        params.projectId = param.selectedProject;
      if (param.selectedStudent && param.selectedStudent !== "all")
        params.studentId = param.selectedStudent;

      return getActiveSandboxList(params);
    },
  });
}
export async function getProjectList(params?: GetActiveSandboxParams) {
  try {
    const baseUrl = getBITSEndApiMap("getActiveSandboxDropdown");

    let user = localStorage.getItem("auth_user");
    if (user === null) throw new Error("User not found in localStorage");
    let userDetail = JSON.parse(user);

    if (userDetail?.role === "admin") {
      const { data } = await api.get<ActiveSandboxDropdown>(baseUrl, {
        params,
      });
      return data;
    } else {
      const { data } = await api.get<ActiveSandboxDropdown>(baseUrl);

      return data;
    }

    // const url = `${baseUrl}`;
  } catch (error) {
    throw error;
  }
}

export interface DropdownOption {
  id: string;
  label: string;
}
export interface ProjectDropdowns {
  projects: DropdownOption[];
  courses: DropdownOption[];
  students: DropdownOption[];
}
export interface CourseListData {
  courses: DropdownOption[];
}

export function useCourseList() {
  return useQuery<CourseListData>({
    // 🔑 Stable key — does NOT include selectedCourse!
    queryKey: ["courseList"], // ← key insight: no param dependency

    queryFn: async () => {
      // Fetch course list — always full list (admin) or user-scoped (non-admin)
      // Note: we pass {} or undefined — no courseId!
      const res = await getProjectList(); // ← no filter!

      const courses = (res.data?.list?.course || [])
        .filter((item: any) => item.id)
        .map((item: any) => ({
          id: item.id as string,
          label: item.name || item.code || "Unnamed Course",
        }));

      return { courses };
    },

    staleTime: 10 * 60 * 1000, // 10 mins — safe for most cases
    // If courses change in-session, add manual refetch or shorter staleTime
  });
}
// ✅ Custom hook — NO async, NO export async function
export function useProjectDropdowns(param: GetActiveSandboxParams) {
  return useQuery<FilteredEntities>({
    queryKey: ["projectLists", param.selectedCourse],
    queryFn: async () => {
      const params: any = {};
      if (param.selectedCourse && param.selectedCourse !== "all")
        params.courseId = param.selectedCourse;
      const items = await getProjectList(params); // ✅ this is fine inside queryFn

      const projectMap = new Map<string, DropdownOption>();
      // const courseMap = new Map<string, DropdownOption>();
      const studentMap = new Map<string, DropdownOption>();

      // 🔍 Ensure you're using the right path: items.data.list?
      // Based on your log: `items` is the full response, and list is in `items.data.list`
      const list = items.data?.list || [];

      list.project.map((item) => {
        if (item?.id != null && item?.title != null) {
          projectMap.set(item.id as string, {
            id: item.id as string,
            label: item.title as string,
          });
        }
      });
      list.student.map((item) => {
        if (item?.id != null && item?.name != null) {
          studentMap.set(item.id as string, {
            id: item.id as string,
            label: item.name as string,
          });
        }
      });

      return {
        projects: Array.from(projectMap.values()),
        // courses: Array.from(courseMap.values()),
        students: Array.from(studentMap.values()),
      };
    },
    // Optional: cache for stability
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
export async function stopSandboxById(params: {
  user_id: string;
  session_id: string;
}): Promise<void> {
  try {
    const baseUrl = getBITSEndApiMap("jupterSandboxStopById");
    const url = `${baseUrl}`;
    await api.post(url, params);
  } catch (err) {
    throw err;
  }
}
